import { ValueFormatterParams } from "ag-grid-community";
import React, { useCallback, useRef, useState } from "react";
import PlusButtonIcon from "../../../../assets/images/PlusButtonIcon.svg";
import Grid from "../../../../components/Grid";
import { ColumnDef } from "../../../../components/Grid/model/Grid";
import ModalDelete from "../../../../components/ModalDelete";
import Loading from "../../../../core/common/Loading";
import api from "../../../../services/api";
import { formatDateTimeBR } from "../../../../helpers/format";

import { useModal } from "../../../../hooks/ModalContext";
import CreateOperacaoPatioGuichesMovimentacao from "./Create";
import Info from "./Info";
import { IOperacoesPatioGuichesMovimentacao } from "./types/types";
import ModalSangria from "./components/ModalSangria";
import ModalFechamento from "./components/ModalFechamento";
import { generateFechamentoPdf } from "./components/ModalFechamento/pdf";
import { generateSangriaPdf } from "./components/ModalSangria/pdf";
import MoneyBillIcon from "../../../../assets/images/moneyBillIcon.svg";
import BankBoxIcon from "../../../../assets/images/bankBoxIcon.svg";
import PrinterIcon from '../../../../assets/images/printerIcon.png';
import { FrontendNotification, NotificationType } from "../../../../shared/Notification";

interface Props {
  guicheId?: string | number | null;
  isModal?: boolean;
}

const ListOperacoesPatioGuichesMovimentacao: React.FC<Props> = ({ guicheId: propGuicheId, isModal = false }) => {
  const [columns] = useState<ColumnDef[]>([
    {
      field: "abertura_data_hora",
      headerName: "Data de Abertura",
      filter: true,
      flex: 1,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return formatDateTimeBR(params.value);
        }
        return "---";
      },
    },
    {
      field: "abertura_valor_caixa",
      headerName: "Valor do Caixa Abertura",
      filter: true,
      flex: 1,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value !== undefined && params.value !== null) {
           return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(params.value);
        }
        return "---";
      },
    },
    {
      field: "fechamento_data_hora",
      headerName: "Data de Fechamento",
      filter: true,
      flex: 1,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return formatDateTimeBR(params.value);
        }
        return "---";
      },
    },
    {
      field: "fechamento_valor_caixa",
      headerName: "Valor do Caixa Fechamento",
      filter: true,
      flex: 1,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value !== undefined && params.value !== null) {
           return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(params.value);
        }
        return "---";
      },
    },
    {
      field: "usuario",
      headerName: "Usuário",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
  ]);
  const [selectedRow, setSelectedRow] = useState<IOperacoesPatioGuichesMovimentacao>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isView, setIsView] = useState<boolean>(false);
  const [isRemove, setIsRemove] = useState<boolean>(false);
  const [isSangria, setIsSangria] = useState<boolean>(false);
  const [isFechamento, setIsFechamento] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const gridRef = useRef<any>(null);
  const { isModalOpen, closeModal, openModal } = useModal();

  const urlParams = new URLSearchParams(globalThis.location.search);
  const guicheId = propGuicheId || urlParams.get("guicheId");

  const onDelete = useCallback(async (entityId?: number) => {
    try {
      setLoading(true);

      const body = {
        id_operacao_patio_guiche_movimentacao: entityId,
      };

      await api.post("/deletar/operacaoPatioGuichesMovimentacao", body);

      setLoading(false);

      setIsRemove(false);

      gridRef.current?.refresh();
    } catch {
      setLoading(false);
    }
  }, []);

  const reprintFechamento = useCallback(async (movimentacao: IOperacoesPatioGuichesMovimentacao) => {
    try {
      if (!movimentacao?.id_operacao_patio_guiche_movimentacao || !movimentacao?.fechamento_data_hora) {
        FrontendNotification("Movimentacao ainda nao foi fechada.", NotificationType.WARNING);
        return;
      }

      setLoading(true);

      const resumoResponse = await api.post("/operacaoPatioGuichesMovimentacao/resumo", {
        id_operacao_patio_guiche_movimentacao: movimentacao.id_operacao_patio_guiche_movimentacao,
      });

      generateFechamentoPdf({
        movimentacao,
        resumo: resumoResponse.data,
        fechamento: {
          dataFechamento: movimentacao.fechamento_data_hora,
          valorFechamento: movimentacao.fechamento_valor_caixa ?? 0,
          justificativa: movimentacao.fechamento_justificativa ?? "",
        },
      });
    } catch (error: any) {
      FrontendNotification(error.response?.data?.error_msg || error.response?.data?.message || "Erro ao reabrir impressao do fechamento", NotificationType.ERROR);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <>
      <Loading loading={loading} />
      {isModalOpen && (
        <CreateOperacaoPatioGuichesMovimentacao
          isView={isView}
          isEdit={isEdit}
          selectedRow={selectedRow}
          onClear={() => {
            closeModal();
            setIsEdit(false);
          }}
          onConfirm={() => {
            setRefreshKey((prev) => prev + 1);
            closeModal();
          }}
          guicheId={guicheId}
        />
      )}



      {isRemove && (
        <ModalDelete
          onCancel={() => setIsRemove(!isRemove)}
          onConfirm={() => onDelete(selectedRow?.id_operacao_patio_guiche_movimentacao)}
          row={String(selectedRow?.id_operacao_patio_guiche_movimentacao)}
        />
      )}

      {isView && (
        <Info
          data={selectedRow}
          title="Conhecer - Operações Pátio Guichês Movimentações"
          onClose={() => setIsView(!isView)}
        />
      )}

      {isSangria && (
        <ModalSangria
          onClose={() => setIsSangria(false)}
          onConfirm={async (valor, justificativa) => {
            try {
              setLoading(true);
              const response = await api.post("/cadastrar/operacaoPatioGuichesMovimentacaoSangria", {
                id_operacao_patio_guiche_movimentacao: selectedRow?.id_operacao_patio_guiche_movimentacao,
                valor_sangria: valor,
                justificativa,
              });
              generateSangriaPdf({
                sangria: response.data,
                id_operacao_patio_guiche: selectedRow?.id_operacao_patio_guiche,
              });
              setRefreshKey((prev) => prev + 1);
              setIsSangria(false);
              FrontendNotification("Sangria realizada com sucesso!", NotificationType.SUCCESS);
            } catch (error: any) {
              FrontendNotification(error.response?.data?.error_msg || error.response?.data?.message || "Erro ao realizar sangria", NotificationType.ERROR);
            } finally {
              setLoading(false);
            }
          }}
        />
      )}

      {isFechamento && (
        <ModalFechamento
          onClose={() => setIsFechamento(false)}
          onConfirm={async (data) => {
            try {
              setLoading(true);
              await api.post("/cadastrar/operacaoPatioGuichesMovimentacaoFechamento", {
                id_operacao_patio_guiche_movimentacao: selectedRow?.id_operacao_patio_guiche_movimentacao,
                fechamento_valor_caixa: data.valorFechamento,
                fechamento_justificativa: data.justificativa,
              });

              const resumoResponse = await api.post("/operacaoPatioGuichesMovimentacao/resumo", {
                id_operacao_patio_guiche_movimentacao: selectedRow?.id_operacao_patio_guiche_movimentacao,
              });

              generateFechamentoPdf({
                movimentacao: selectedRow,
                resumo: resumoResponse.data,
                fechamento: {
                  dataFechamento: data.dataFechamento,
                  valorFechamento: data.valorFechamento,
                  justificativa: data.justificativa,
                },
              });

              setRefreshKey((prev) => prev + 1);
              setIsFechamento(false);
              FrontendNotification("Fechamento realizado com sucesso!", NotificationType.SUCCESS);
            } catch (error: any) {
              FrontendNotification(error.response?.data?.error_msg || error.response?.data?.message || "Erro ao realizar fechamento", NotificationType.ERROR);
            } finally {
              setLoading(false);
            }
          }}
          data={selectedRow}
        />
      )}

      <div className={`flex flex-col w-full ${isModal ? "h-full" : "h-screen"} bg-[#F5F5F5] p-5`}>
        <div className="flex items-center justify-between w-full mb-3">
          <div>
            <h1 className="text-2xl text-[#000000] font-bold">Guichês - Movimentações</h1>
          </div>
          <div className="mr-14">
            <button
              className="flex items-center justify-center h-12 w-36 bg-[#062D4E] text-[#FFFFFF] text-sm font-light border-none rounded-full"
              onClick={() => {
                setIsEdit(false);
                setSelectedRow(undefined);
                openModal();
              }}
            >
              <img src={PlusButtonIcon} className="mr-2" alt="" />
              <span>Adicionar</span>
            </button>
          </div>
        </div>
        <div className={`flex ${isModal ? "w-full h-full" : "w-full"}`}>
          <Grid
            ref={gridRef}
            key={refreshKey}
            // Tela não está nos menus: informa a chave para as permissões
            // (SALVAR/REMOVER/CONHECER) seguirem o módulo "Guichês".
            permissionKey="guiches"
            columns={columns}
            filters={[]}
            pagination
            path="/listar/operacaoPatioGuichesMovimentacao"
            customFilters={{ id_operacao_patio_guiche: guicheId }}
            onView={(data: IOperacoesPatioGuichesMovimentacao) => {
              setSelectedRow(data);
              setIsView(!isView);
            }}
            customButtons={[
              {
                label: "Sangria",
                icon: () => MoneyBillIcon,
                action: (data: IOperacoesPatioGuichesMovimentacao) => {
                  setSelectedRow(data);
                  setIsSangria(true);
                },
              },
              {
                label: "Fechamento",
                icon: () => BankBoxIcon,
                action: (data: IOperacoesPatioGuichesMovimentacao) => {
                  setSelectedRow(data);
                  setIsFechamento(true);
                },
                shouldShow: (data: IOperacoesPatioGuichesMovimentacao) => !data?.fechamento_data_hora,
              },
              {
                label: "Reimpressao",
                icon: () => PrinterIcon,
                action: (data: IOperacoesPatioGuichesMovimentacao) => {
                  setSelectedRow(data);
                  reprintFechamento(data);
                },
                shouldShow: (data: IOperacoesPatioGuichesMovimentacao) => Boolean(data?.fechamento_data_hora),
              },
            ]}
            status={[]}
          />
        </div>
      </div>
    </>
  );
};

export default ListOperacoesPatioGuichesMovimentacao;

